import { IonButton, IonPopover, IonText } from '@ionic/react';
import { FC, useState } from 'react';
import { AngleDownIcon, ArrowRightArrowLeftIcon } from '../theme/Icons';
import { agencyTabSearch } from '../organizations/agencyTabs';
import { createUseStyles } from 'react-jss';
import { MemberAvatar } from '../household/MemberAvatar';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { Text } from '../components/Text';
import { MenuItem } from '../components/MenuItem';
import { CareMapMenuItem } from '../caremap/CareMapMenuItem';
import { Divider } from '../components/Divider';
import { ICareMap } from '../caremap/ICareMap';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CompositeAnonymousHttpClient } from '../auth/anonymous/CompositeAnonymousHttpClient';
import dayjs from 'dayjs';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { AddCareMapButton } from '../caremap/AddCareMapButton';
import { CreateAccountButton } from '../auth/CreateAccountButton';
import { Href } from '../components/Href';
import { AuthHandler } from '../auth/AuthHandler';
import { mediaMinWidth } from '../theme/utils';
import { useSwitchCareMap } from '../utils/useSwitchCareMap';
import { RecordingState } from '../recording/RecordingState';
import { Logger } from '../support/Logger';
import { AlertPresenter } from '../AlertBinder';
import { isNoCareMapAccessError, NO_CARE_MAP_ACCESS_ALERT } from '../caremap/noCareMapAccess';
import { ISignInTrackingFrom } from '../auth/ISignInTrackingFrom';

const useStyles = createUseStyles({
    wrapper: {
        // we use this component on CareMapPage where there is no ionic container,
        // so we need to specify the font explicitly to keep it consistent everywhere
        fontFamily: 'var(--ion-font-family)',

        width: 'fit-content',

        margin: '8px 4px 10px',

        minWidth: 210,
        [mediaMinWidth.sm]: {
            minWidth: 240,
        }
    },

    switcher: {
        borderRadius: 8,
        background: '#fff',
        boxShadow: '1px 1px 6px #9a9a9a',

        '& ion-button': {
            margin: 0,
            display: 'flex'
        }
    },

    // The empty-state buttons keep their usual outline-and-primary styling; they only need a solid
    // white fill so the blue text and border stay readable when the switcher sits on a video frame
    // instead of a page background. White matches the populated switcher above, so both states of
    // this slot read as the same control.
    onImageryButton: {
        '& ion-button': {
            '--background': '#fff',
            '--background-hover': '#fff',
            '--background-focused': '#fff',
            '--background-activated': '#fff'
        }
    },

    inner: {
        flex: 1,
        gap: 12,
        display: 'flex',
        maxWidth: '100%',
        alignItems: 'center',

        '& > ion-text': {
            overflow: 'hidden',
            paddingRight: 10,

            '& > div': {
                fontSize: 14,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis'
            }
        }
    },

    avatarWrapper: {
        borderRadius: '50%',
        border: '2px solid white',
        boxShadow: '1px 1px 6px #9a9a9a'
    },

    recipientMenuPopover: {
        '& hr': {
            marginBlockStart: 0,
            marginBlockEnd: 0
        },

        '&::part(content)': {
            minWidth: 350,
            maxWidth: 350
        }
    },

    expiration: {
        margin: '10px 0 0',
        fontSize: 13,
        overflowWrap: 'anywhere',

        '& > ion-text': {
            textWrap: 'nowrap',
            marginRight: 8
        },

        '& > a': {
            textWrap: 'nowrap'
        }
    },

    // same scrim treatment as the banner's welcome message, so the expiry text and Create
    // Account link stay legible over a video frame
    expirationOnImagery: {
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: 10,
        padding: '6px 10px',
        width: 'fit-content',

        '& > a': {
            color: 'white',
            textDecoration: 'underline'
        }
    }
});

export const AnonymousSessionExpirationHint: React.FC<{ careMapId: string; userId: string; onImagery?: boolean; }> = ({ careMapId, userId, onImagery }) => {
    const client = useInstance(CompositeAnonymousHttpClient);
    const expirationEpochSeconds = client.getAccessTokenExpirationForCaremap({ careMapId, userId });
    const expirationISO = new Date(expirationEpochSeconds * 1000).toISOString();

    // A Care Code grants access for a year, so "Aug 17" alone would be ambiguous. Only spell the
    // year out when it differs from this one, to keep the common short-link case terse.
    const expiresInAnotherYear = dayjs(expirationISO).year() !== dayjs().year();
    const dateFormat = expiresInAnotherYear ? 'MMM D, YYYY' : 'MMM D';

    return (
        <IonText data-type="caremap-expiration" color={onImagery ? 'light' : 'medium'} style={{fontSize: 12}}>
            Access expires {dayjs(expirationISO).format(dateFormat)}, {dayjs(expirationISO).format('h:mm a')}
        </IonText>
    );
};

export const CreateAccountLink: React.FC<{trackingFrom: ISignInTrackingFrom}> = ({ trackingFrom }) => {
    const authHandler = useInstance(AuthHandler);

    const onClick = async () => {
        await authHandler.login({ trackingFrom });
    };

    return (
        <Href data-id="create-account-link" onClick={onClick}>
            Create Account
        </Href>
    );
};

type ICareRecipientSwitcherProps = {
    triggerId?: string;
    trackingFrom?: ISignInTrackingFrom;
    onClick?: () => void;
    createAccountMsg?: string;
    addRecipientMsg?: string;
    // Set when this instance sits on top of a photo/video rather than a page background. The
    // empty-state buttons are transparent outlines, so their blue text and border wash out against
    // bright imagery; filling them white keeps the usual button styling legible on any frame. The
    // explanatory caption is dropped for the same reason: unstyled body text is unreadable there.
    onImagery?: boolean;
};

export const CareRecipientSwitcher: FC<ICareRecipientSwitcherProps> = ({ triggerId = 'switch-recipient-button', trackingFrom = 'Recipient Switcher', onClick, createAccountMsg, addRecipientMsg, onImagery }) => {
    const classes = useStyles();

    const tracking = useInstance(MixPanelEventEmitter);
    const history = useInstance(HistoryViewModel);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);
    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);
    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const switchCareMap = useSwitchCareMap();
    const logger = useInstance(Logger);
    const alert = useInstance(AlertPresenter);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const activeCareMap = careMapState.getActiveCaremap();

    const orgId = (orgState.isOrgTeamMember() ? orgState.getMostRecentOrganization()?.orgId : undefined);

    // CareMapState only holds the care maps loaded for this user, which for agency staff is just the
    // ones they happen to have created. So the switcher reads the org's roster, keeps the clients this
    // person can open, and sorts them by name, which is how a case manager looks someone up.
    // setActiveCaremap falls back to getCareMapFromOrg for anything not already loaded, so switching
    // to a client this user has never opened works.
    const orgCareMaps = orgId
        ? orgState.getOrganizationCareMaps(orgId)
            .filter(map => orgState.canViewClientCareMap(map))
            .sort((a, b) => careMapState.getPatientFullName(a).localeCompare(careMapState.getPatientFullName(b)))
        : [];

    // bubble incomplete to the top
    const personalCareMaps = careMapState.getCareMaps().sort((a, b) => {
        const aIncomplete = careMapState.isIncomplete(a);
        const bIncomplete = careMapState.isIncomplete(b);
        return (aIncomplete && bIncomplete) || (!aIncomplete && !bIncomplete) ? 0 : aIncomplete ? -1 : 1;
    });

    const careMaps = orgId ? orgCareMaps : personalCareMaps;

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    if (isAnonymous && !activeCareMap) {
        return (
            <div style={{padding: '10px 15px 5px 5px'}} className={onImagery ? classes.onImageryButton : undefined}>
                <CreateAccountButton trackingFrom={trackingFrom} />
                {!onImagery && <p>{createAccountMsg}</p>}
            </div>
        );
    }

    // if there's no caremaps, no active one or just one and it's incomplete, show button instead
    if (!careMaps.length || !activeCareMap || (careMaps.length === 1 && careMapState.isIncomplete(careMaps[0]))) {
        return (
            <div style={{padding: '10px 15px 5px 5px'}} className={onImagery ? classes.onImageryButton : undefined}>
                <AddCareMapButton trackingFrom="recipient-switcher-empty" />
                {!onImagery && <p>{addRecipientMsg}</p>}
            </div>
        );
    };

    // Switching care recipients mid-recording would attach the recording to the wrong caremap
    // and likely make it invisible to the user later. Disable the trigger while recording.
    const isRecording = recordingState.isRecording();

    const toggleRecipientMenu = () => {
        setIsOpen(oldState => {
            return !oldState;
        });
    };

    const onButtonClick = () => {
        if (onClick) {
            onClick();
        }

        tracking.track(orgId ? 'Client Switcher Initiated' : 'Care Recipient Switcher Opened');
        toggleRecipientMenu();
    };

    const switchRecipient = async (careMap: ICareMap) => {
        if (activeCareMap?.careMapId !== careMap.careMapId) {
            setIsOpen(false);
            tracking.track('Care Recipient Switched');
            try {
                await switchCareMap(careMap.careMapId);
            } catch (err: any) {
                if (!isNoCareMapAccessError(err)) {
                    logger.error(err);
                    return;
                }
                alert.showAlertV2(NO_CARE_MAP_ACCESS_ALERT);
            }
        }
    };

    return (
        <>
            <div data-id="recipient-switcher" className={classes.wrapper}>
                {orgId && (
                    <div data-type="org-name-heading" style={{padding: '2px 0 8px'}}>
                        <Text type="title">
                            {orgState.getOrganization(orgId)?.name || ''}
                        </Text>
                    </div>
                )}
                <div className={classes.switcher}>
                    <IonButton
                        fill="clear"
                        id={triggerId}
                        onClick={onButtonClick}
                        data-type={orgId ? 'org-switcher' : undefined}
                        disabled={(isAnonymous && careMaps.length === 1) || isRecording}
                    >
                        <div className={classes.inner}>
                            <div className={classes.avatarWrapper}>
                                <MemberAvatar
                                    xsmall
                                    member={activeCareMap?.patient}
                                />
                            </div>
                            <Text color="dark">
                                <div className="mp-mask">
                                    {careMapState.getPatientFullName() || 'New Care Recipient'}
                                </div>
                            </Text>
                        </div>
                        {orgId ? (
                            <ArrowRightArrowLeftIcon slot="end" color="dark" />
                        ): (
                            <AngleDownIcon slot="end" color="dark" />
                        )}
                    </IonButton>
                </div>

                {isAnonymous && activeCareMap && (
                    <div className={`${classes.expiration} ${onImagery ? classes.expirationOnImagery : ''}`}>
                        <AnonymousSessionExpirationHint
                            careMapId={activeCareMap.careMapId!}
                            userId={(activeCareMap as ICareMap).userId}
                            onImagery={onImagery}
                        />
                        <CreateAccountLink trackingFrom={trackingFrom} />
                    </div>
                )}
            </div>

            <IonPopover
                isOpen={isOpen}
                alignment="start"
                trigger={triggerId}
                data-id="recipient-switcher-menu"
                onDidDismiss={() => setIsOpen(false)}
                className={classes.recipientMenuPopover}
            >
                <MenuItem data-id="switcher-recipient-label" label={orgId ? 'Clients' : 'Care Recipient'} heading />
                {careMaps.map((map) => {
                    return map.careMapId !== activeCareMap?.careMapId ? (
                        <div key={map.careMapId}>
                            <CareMapMenuItem
                                careMap={map}
                                onClick={switchRecipient}
                            />
                            <Divider />
                        </div>
                    ) : null;
                })}

                {orgId ? (
                    // a five-client roster fits in the menu, but a real caseload runs to fifty, so
                    // keep a route through to the searchable list rather than making this the only way in
                    <MenuItem
                        data-id="switcher-see-all-clients"
                        label="See all clients"
                        onClick={() => {
                            setIsOpen(false);
                            history.push(`/caregiver?${agencyTabSearch('clients')}`);
                        }}
                    />
                ) : !isAnonymous ? (
                    <div style={{padding: 10}}>
                        <AddCareMapButton
                            trackingFrom="recipient-switcher-menu"
                            closeMenu={toggleRecipientMenu}
                        />
                    </div>
                ) : (
                    <div style={{textAlign: 'center', padding: 16}}>
                        <CreateAccountLink trackingFrom={trackingFrom} />
                    </div>
                )}
            </IonPopover>
        </>
    );
};
